import React, { useState } from "react";
import { Button, ButtonVariants } from "../../../components/core/Button/Button";
import { Modal } from "../../../components/core/Modal/Modal";
import {
  ADD_NEW_TECH_SKILL,
  CANCEL_LABEL,
  DATA_ADDED_SUCCESS_LABEL,
  EDIT_TECH_SKILL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  SAVE_LABEL,
  TECH_SKILL_DUPLICATE_ENTITY_ERROR_MESSAGE,
  TECH_SKILL_DUPLICATE_PROP_ERROR_MESSAGE,
} from "../../../constants/Dictionary";
import {
  StyledAlertMessage,
  StyledTechSkillModal,
} from "./TechSkillModal.styled";
import { ReactComponent as SaveIcon } from "../../../icons/save.svg";
import { ReactComponent as CancelIcon } from "../../../icons/close.svg";
import { Loader } from "../../../components/core/Loader/Loader";
import { TechSkill } from "../../../types/api/techSkills.type";
import { useNotification } from "../../../hooks/notification.hook";
import { AxiosError } from "axios";
import {
  StatusCodes,
  CustomerErrorCodesEnum,
} from "../../../constants/AppConstants";
import { SingleErrorResponse } from "../../../types/api/generic.type";
import { IconManager } from "../../../components/core/IconManager/IconManager";
import { BodyTyp } from "../../../components/Typography/Typography";
import { theme } from "../../../constants/StyledComponents";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import { ReactComponent as WarningIcon } from "../../../icons/warning.svg";
import { FormDataTech } from "../../CollaboratorDetail/CollaborProfileTabs/ProfessionalHistory/Skills/Techniques/CollaboratorTechSkillsModal/CollaboratorTechSkillsModal";
import { UseFormSetError } from "react-hook-form";
import { TechSkillForm } from "../../CollaboratorDetail/CollaborProfileTabs/ProfessionalHistory/Skills/Techniques/components/TechSkillsForm";
import {
  useAddTechSkill,
  useUpdateTechSkill,
} from "../../../hooks/techSkills.hooks";

type TechSkillModalProps = {
  closeModal: () => void;
  onSave: () => void;
  record?: TechSkill;
};

export const TechSkillModal = ({
  closeModal,
  record,
  onSave,
}: TechSkillModalProps) => {
  const { notification } = useNotification();
  const [isDuplicateError, setIsDuplicateError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const convertDuplicatedPropertyMessageToArray = (message: string) => {
    const mapping = {
      COMPETENCE: "competence",
      SOFTWARE: "software",
      STREAM: "stream",
      COMPONENT: "component",
    };

    // eslint-disable-next-line no-useless-escape
    const keys = message.replace(/[\[\]\s]/g, "").split(",");
    return keys.map((key) => mapping[key as keyof typeof mapping]);
  };
  const { addTechSkill, isAdding } = useAddTechSkill();
  const { updateTechSkill, isUpdating } = useUpdateTechSkill();
  const isSubmitting = isAdding || isUpdating;

  const onSubmit = async (
    data: FormDataTech,
    setError: UseFormSetError<FormDataTech>,
  ) => {
    try {
      setIsDuplicateError(false);
      const body = {
        competence: data.competence,
        stream: data.stream,
        software: data.software,
        component: data.component ?? "",
        description: data.description ?? "",
      };

      if (record) {
        await updateTechSkill({ id: record.id, body });
      } else {
        await addTechSkill(body);
        notification.success(DATA_ADDED_SUCCESS_LABEL, undefined, "toast");
      }
      closeModal();
      onSave();
    } catch (error) {
      const errorObj = error as AxiosError<SingleErrorResponse>;
      if (
        errorObj.response &&
        errorObj.response.status === StatusCodes.CUSTOM_ERROR
      ) {
        if (
          errorObj.response.data.code ===
          CustomerErrorCodesEnum.DUPLICATED_ENTITY
        ) {
          setIsDuplicateError(true);
        } else if (
          errorObj.response.data.code ===
          CustomerErrorCodesEnum.TECH_SKILL_DUPLICATED_PROPERTY
        ) {
          const errorPropertyList = convertDuplicatedPropertyMessageToArray(
            errorObj.response.data.message,
          );

          errorPropertyList.forEach((property) => {
            setError(property as keyof FormDataTech, {
              type: "custom",
              message: TECH_SKILL_DUPLICATE_PROP_ERROR_MESSAGE,
            });
          });
        }
      } else {
        console.error(`Error updating technical skills: ${error}`);
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
        closeModal();
      }
    }
  };

  return (
    <Modal
      label={record ? `${EDIT_TECH_SKILL}` : `${ADD_NEW_TECH_SKILL}`}
      isOpen
      {...(isDuplicateError && {
        message: (
          <StyledAlertMessage>
            <IconManager size={IconSizeEnum.SMALL} color={theme.colors.red_1}>
              <WarningIcon />
            </IconManager>
            <BodyTyp
              color={theme.colors.red_1}
              fontWeight={FontWeightEnum.SEMIBOLD}
            >
              {TECH_SKILL_DUPLICATE_ENTITY_ERROR_MESSAGE}
            </BodyTyp>
          </StyledAlertMessage>
        ),
      })}
      rightButton={
        <Button
          icon={<SaveIcon />}
          label={SAVE_LABEL}
          disabled={!isDirty || isSubmitting}
          type="submit"
          form="techSkillsForm"
        />
      }
      leftButton={
        <Button
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          label={CANCEL_LABEL}
          onClick={closeModal}
          disabled={isSubmitting}
        />
      }
    >
      <StyledTechSkillModal>
        <Loader loading={isSubmitting}>
          <TechSkillForm
            onSubmit={onSubmit}
            formID="techSkillsForm"
            record={record}
            setIsDirty={setIsDirty}
            isLevelRequired={false}
          />
        </Loader>
      </StyledTechSkillModal>
    </Modal>
  );
};
